import React, { useMemo, memo } from 'react';
import { useInfiniteScroll } from './hooks/useInfiniteScroll';
import { Link } from '../Link/Link';
import { IrfAlertMessages } from './index';
import { IrfMessages as messages } from './IrfMessages';
import { InternalIrfPublicationProps } from './utils/fetchData';
import { useIntl } from 'react-intl';
import { getOrderedTypeCodes, getGroupTitleMessageKey } from './IrfConfig';

/**
 * Processes IEEE citation entries to replace static [1] with consecutive numbering
 * @param citationEntry - The HTML citation entry from the API
 * @param index - The zero-based index for consecutive numbering
 * @returns Processed citation entry with correct IEEE numbering
 */
const processIEEECitation = (citationEntry: string, index: number): string => {
  if (!citationEntry || !citationEntry.includes('csl-left-margin')) {
    return citationEntry;
  }

  // Replace [1] in IEEE citations with consecutive numbering
  return citationEntry.replace(
    /(<div class="csl-left-margin">)\[(\d+)\](<\/div>)/,
    `$1[${index + 1}]$3`,
  );
};

interface RenderEntriesProps {
  entries: InternalIrfPublicationProps[];
  peerReviewedCache: Record<string, boolean>;
  activeTab: string;
  showOnlyPeerReviewed: boolean;
  getExcludedPublications: string[];
  isEditMode: boolean;
  dataState: string;
  jsonData: InternalIrfPublicationProps[];
  displayContentGrouped: string;
}

// Separate component for non-grouped content with infinite scroll
const NonGroupedContent: React.FC<{
  entries: InternalIrfPublicationProps[];
  displayContentGrouped: string;
}> = memo(({ entries, displayContentGrouped }) => {
  const { displayedItems, loadMoreRef, hasMore } = useInfiniteScroll(
    entries,
    10,
  );

  return (
    <>
      {displayedItems.map((entry, index) => (
        <EntryItem
          key={`${entry.url_handle}-${index}`}
          entry={entry}
          showType={displayContentGrouped === 'group_by_type'}
          index={index}
        />
      ))}
      {hasMore && (
        <div
          ref={loadMoreRef}
          style={{ height: '20px', margin: '20px 0' }}
          className="load-more-trigger"
          aria-hidden="true"
        />
      )}
    </>
  );
});

NonGroupedContent.displayName = 'NonGroupedContent';

interface EntryItemProps {
  entry: InternalIrfPublicationProps;
  showType: boolean;
  index?: number;
}

const EntryItem: React.FC<EntryItemProps> = memo(
  ({ entry, showType, index = 0 }) => {
    const entryType = entry.type?.split('-').slice(1).join('').trim() || '';

    // Process IEEE citation numbering if needed
    const processedCitation = useMemo(() => {
      return processIEEECitation(entry.citation_entry || '', index);
    }, [entry.citation_entry, index]);

    return (
      <li className="teaser">
        <Link
          className="teaser-link"
          href={entry.url_handle}
          title={entryType}
          newTab
          aria-label={`${entry.type} - ${entry.citation_entry?.replace(/<[^>]*>/g, '')}`}
        >
          <div className="teaser-wrapper">
            {showType && <h3>{entryType}</h3>}
            {entry.peer_reviewed && (
              <span
                className="peer-reviewed"
                aria-label="Peer-Reviewed Publication"
                role="status"
              >
                Peer-Reviewed
              </span>
            )}
            <div
              className="irf-content"
              dangerouslySetInnerHTML={{
                __html: processedCitation,
              }}
            />
          </div>
        </Link>
      </li>
    );
  },
);

export const RenderEntries: React.FC<RenderEntriesProps> = memo(
  ({
    entries,
    peerReviewedCache,
    activeTab,
    showOnlyPeerReviewed,
    getExcludedPublications,
    isEditMode,
    dataState,
    jsonData,
    displayContentGrouped,
  }) => {
    const intl = useIntl();

    const filteredAndGroupedEntries = useMemo(() => {
      const filtered = entries.filter(
        (entry) =>
          (!peerReviewedCache[activeTab] || entry.peer_reviewed === true) &&
          (activeTab === 'projectTab' ||
            !showOnlyPeerReviewed ||
            entry.peer_reviewed) &&
          !getExcludedPublications.includes(entry.url_handle?.toString() || ''),
      );

      if (displayContentGrouped === 'group_by_type') {
        // First, group entries by their type code (extracted from entry.type)
        const groupedByTypeCode = filtered.reduce(
          (acc, entry) => {
            // Extract type code from entry.type (format: "CODE-Description")
            const typeCode = entry.type?.split('-')[0]?.trim() || '';
            if (!acc[typeCode]) acc[typeCode] = [];
            acc[typeCode].push(entry);
            return acc;
          },
          {} as Record<string, InternalIrfPublicationProps[]>,
        );

        // Create ordered groups based on IrfConfig priority, grouped by message key
        const orderedTypeCodes = getOrderedTypeCodes();
        const orderedGroupedByMessageKey: Record<
          string,
          { entries: InternalIrfPublicationProps[]; typeCode: string }
        > = {};

        // Add groups in the order defined by IrfConfig, grouped by message key
        orderedTypeCodes.forEach((typeCode) => {
          if (groupedByTypeCode[typeCode]) {
            // Use the message key as the grouping key to consolidate related types
            const messageKey = getGroupTitleMessageKey(typeCode);
            if (!orderedGroupedByMessageKey[messageKey]) {
              orderedGroupedByMessageKey[messageKey] = {
                entries: [],
                typeCode: typeCode, // Use the first typeCode encountered for this message key
              };
            }
            // Add all entries for this type code to the consolidated group
            orderedGroupedByMessageKey[messageKey].entries.push(
              ...groupedByTypeCode[typeCode],
            );
          }
        });

        // Add any remaining groups that weren't in the config (fallback)
        Object.entries(groupedByTypeCode).forEach(([typeCode, entries]) => {
          if (!orderedTypeCodes.includes(typeCode)) {
            const messageKey = getGroupTitleMessageKey(typeCode);
            if (!orderedGroupedByMessageKey[messageKey]) {
              orderedGroupedByMessageKey[messageKey] = {
                entries: entries,
                typeCode: typeCode,
              };
            } else {
              // Add to existing group if message key already exists
              orderedGroupedByMessageKey[messageKey].entries.push(...entries);
            }
          }
        });

        return { filtered, grouped: orderedGroupedByMessageKey };
      }

      return { filtered, grouped: null };
    }, [
      entries,
      peerReviewedCache,
      activeTab,
      showOnlyPeerReviewed,
      getExcludedPublications,
      displayContentGrouped,
    ]);

    if (filteredAndGroupedEntries.filtered.length === 0) {
      return (
        <li className="no-peer-reviewed-message" role="alert">
          <p>{intl.formatMessage(messages.no_peer_reviewed_message)}</p>
        </li>
      );
    }

    return (
      <>
        {isEditMode === true && dataState === 'utd' ? (
          <IrfAlertMessages
            dataState={dataState}
            totalEntrys={jsonData.length}
          />
        ) : (
          <>
            {isEditMode === true && <IrfAlertMessages dataState={dataState} />}
          </>
        )}

        {/* Grouped content */}
        {displayContentGrouped === 'group_by_type' &&
          filteredAndGroupedEntries.grouped &&
          (() => {
            let globalIndex = 0; // Track consecutive numbering across all groups
            return Object.entries(filteredAndGroupedEntries.grouped).map(
              ([messageKey, groupData]) => {
                const typedGroupData = groupData as {
                  entries: InternalIrfPublicationProps[];
                  typeCode: string;
                };
                const groupTitle =
                  messageKey && (messages as any)[messageKey]
                    ? intl.formatMessage((messages as any)[messageKey])
                    : messageKey; // Fallback to message key

                // Skip group titles for Projects and Presentations
                const shouldShowGroupTitle =
                  messageKey !== 'Projects' && messageKey !== 'Presentations';

                return (
                  <React.Fragment key={messageKey}>
                    {shouldShowGroupTitle && <h3>{groupTitle}</h3>}
                    {typedGroupData.entries.map((entry, localIndex) => {
                      const currentGlobalIndex = globalIndex++;
                      return (
                        <EntryItem
                          key={`${entry.url_handle}-${localIndex}`}
                          entry={entry}
                          showType={false}
                          index={currentGlobalIndex}
                        />
                      );
                    })}
                  </React.Fragment>
                );
              },
            );
          })()}

        {displayContentGrouped !== 'group_by_type' && (
          <NonGroupedContent
            entries={filteredAndGroupedEntries.filtered}
            displayContentGrouped={displayContentGrouped}
          />
        )}
      </>
    );
  },
);

RenderEntries.displayName = 'RenderEntries';
EntryItem.displayName = 'EntryItem';
